import Badge from '../ui/Badge';

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function AttachmentLink({ attachment }) {
  if (!attachment.downloadUrl) {
    return <span className="text-xs text-foreground-subtle">{attachment.originalName}</span>;
  }
  return (
    <a
      href={attachment.downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs text-accent hover:underline"
    >
      {attachment.originalName}
    </a>
  );
}

/**
 * Shared message-thread renderer for both the investor and admin ticket
 * detail screens — same shape either side (normalizeTicket in
 * ticketsApi.js), so no need for two versions. "Support team" is inferred
 * from the author's roles rather than a dedicated flag, since the API
 * doesn't send one — an author with admin/super-admin among their roles is
 * staff, everyone else is the investor who opened the ticket.
 */
export default function TicketThread({ messages = [] }) {
  if (messages.length === 0) {
    return <p className="py-6 text-center text-foreground-muted">No messages yet.</p>;
  }

  return (
    <div className="space-y-4">
      {messages.map((m) => {
        const isStaff = (m.author?.roles ?? []).some((r) => r === 'admin' || r === 'super-admin');
        return (
          <div key={m.id} className="rounded-xl border border-border bg-background-elevated p-4">
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">{m.author?.fullName || 'Unknown'}</span>
                {isStaff && (
                  <Badge tone="success" className="!px-1.5 !py-0.5 text-[10px]">
                    Support team
                  </Badge>
                )}
              </div>
              <span className="text-xs text-foreground-subtle">{formatDateTime(m.createdAt)}</span>
            </div>
            <p className="text-sm text-foreground whitespace-pre-wrap">{m.body}</p>
            {m.attachments?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3">
                {m.attachments.map((a) => (
                  <AttachmentLink key={a.id} attachment={a} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
